/**
 * Activity Log Helper
 * Centralized dictionaries and utilities for parsing activity log data
 */

// Module to URL path mapping
export const moduleLinks: Record<string, string> = {
  clients: "/clients",
  leads: "/leads",
  lead: "/leads",
  proposals: "/proposals",
  proposal: "/proposals",
  invoices: "/invoices",
  projects: "/projects",
  "quote-requests": "/quote-requests",
  quotes: "/quotes",
  providers: "/providers",
  purchase_orders: "/purchase-orders",
  roles: "/settings/roles",
  dashboard: "/dashboard",
};

// Module to display label mapping
export const moduleLabels: Record<string, string> = {
  clients: "Client List",
  leads: "Leads",
  lead: "Leads",
  proposals: "Proposals",
  proposal: "Proposals",
  invoices: "Invoice List",
  projects: "Project List",
  "quote-requests": "RFQ List",
  quotes: "Quote List",
  providers: "Provider List",
  purchase_orders: "Purchase Order List",
  roles: "Roles",
  dashboard: "Dashboard",
};

// Entity type to URL path mapping (for specific items)
export const entityLinks: Record<string, (id: string) => string> = {
  invoice: (id) => `/invoices/${id}`,
  quote: (id) => `/quotes/${id}`,
  project: (id) => `/projects/${id}`,
  "quote-request": (id) => `/quote-requests/${id}`,
  client: (id) => `/clients/${id}`,
  lead: (id) => `/leads/${id}`,
  proposal: (id) => `/proposals/${id}`,
  provider: (id) => `/providers/${id}`,
  purchase_order: (id) => `/purchase-orders/${id}`,
};

// Entity type to display label mapping
export const entityLabels: Record<string, string> = {
  invoice: "Invoice",
  quote: "Quote",
  project: "Project",
  "quote-request": "RFQ",
  client: "Client",
  lead: "Lead",
  proposal: "Proposal",
  provider: "Provider",
  purchase_order: "PO",
  location: "Site",
  depot: "Depot",
  Announcement: "Announcement",
};

// Action to display text mapping
export const actionLabels: Record<string, string> = {
  // View actions
  view_quote_detail: "viewed",
  view_invoice_detail: "viewed",
  view_project_detail: "viewed",
  view_project_overview: "viewed",
  view_quote_request_detail: "viewed",
  view_quote_request_edit: "viewed",
  view_client_detail: "viewed",
  view_provider_detail: "viewed",
  view_clients_list: "viewed",
  view_invoices_list: "viewed",
  view_projects_list: "viewed",
  view_quote_requests_list: "viewed",
  view_quotes_list: "viewed",
  view_purchase_order_list: "viewed",
  view_dashboard: "viewed",
  view_roles: "viewed",
  view_client_create: "opened",
  view_provider_create: "opened",
  view_leads_list: "viewed",
  view_lead_detail: "viewed",
  view_lead_create: "opened",
  view_lead_import: "opened",

  // Lead actions (backend, module 'lead')
  lead_created: "created lead",
  lead_updated: "updated lead",
  lead_status_changed: "changed status of lead",
  lead_converted: "converted to client",
  quote_link_sent: "sent quote link to lead",
  quote_link_created: "created quote link for lead",
  quote_link_extended: "extended quote link for lead",
  quote_link_revoked: "revoked quote link for lead",
  quote_accepted_public: "quote accepted online by lead",
  attached_to_lead: "attached proposal to lead",
  detached_from_lead: "detached proposal from lead",
  lead_web_intake: "received website inquiry for lead",
  lead_digest_sent: "received the leads-due digest",
  lead_settings_updated: "updated lead settings",
  
  // Create actions
  create_quote: "created",
  create_invoice: "created",
  create_project: "created",
  create_quote_request: "created",
  create_client: "created",
  create_provider: "created",
  create_purchase_order: "created",
  
  // Update actions
  update_quote: "updated",
  update_invoice: "updated",
  update_project: "updated",
  update_quote_request: "updated",
  update_client: "updated",
  
  // Quote specific actions
  accept_quote: "accepted",
  deny_quote: "denied",
  send_quote: "sent",
  send_quote_email: "sent",
  generate_quote_pdf: "generated PDF for",
  convert_to_quote: "converted to quote",
  
  // Invoice specific actions
  send_invoice: "sent",
  send_invoice_email: "sent",
  send_discount_invoice: "sent discount for",
  create_payment: "recorded payment for",
  
  // Project specific actions
  mark_project_photo_for_invoice: "updated photos for",
  
  // Auth actions
  login_success: "logged in",
  logout: "logged out",
  
  // Discount actions
  discount_popup_viewed: "viewed discount popup",
  discount_popup_cancelled: "cancelled discount popup",
  discount_no_reminder: "dismissed discount reminder",
};

// Fallback action labels by actionType
export const actionTypeFallbacks: Record<string, string> = {
  view: "viewed",
  create: "created",
  update: "updated",
  click: "interacted with",
  auth: "authenticated",
};

/**
 * Get the display label for an action
 */
export const getActionLabel = (action: string, actionType: string): string => {
  if (actionLabels[action]) {
    return actionLabels[action];
  }
  if (actionTypeFallbacks[actionType]) {
    return actionTypeFallbacks[actionType];
  }
  return action.replace(/_/g, " ");
};

/**
 * Get the link for an entity
 */
export const getEntityLink = (entityType: string | null, entityId: string | null): string | null => {
  if (!entityType || !entityId) return null;
  const linkFn = entityLinks[entityType];
  return linkFn ? linkFn(entityId) : null;
};

/**
 * Get the display label for an entity type
 */
export const getEntityLabel = (entityType: string | null): string => {
  if (!entityType) return "";
  return entityLabels[entityType] || entityType.charAt(0).toUpperCase() + entityType.slice(1);
};

/**
 * Get the link for a module list view
 */
export const getModuleLink = (module: string | null): string | null => {
  if (!module) return null;
  return moduleLinks[module] || null;
};

/**
 * Get the display label for a module list view
 */
export const getModuleLabel = (module: string | null): string => {
  if (!module) return "";
  return moduleLabels[module] || module.charAt(0).toUpperCase() + module.slice(1).replace(/_/g, " ");
};

/**
 * Check if an activity is a list view (no specific entity)
 */
export const isListViewActivity = (entityType: string | null, module: string | null): boolean => {
  return !entityType && !!module;
};
